import { useCallback, useState } from "react"
import { ImageGalleryItem } from "@/shared/components/image-gallery/types"

interface UseGalleryMediaActionsParams {
  items: ImageGalleryItem[]
  currentIndex: number
  onError: (message: string) => void
  onInfo: (message: string) => void
}

const useGalleryMediaActions = ({ items, currentIndex, onError, onInfo }: UseGalleryMediaActionsParams) => {
  const [isDownloading, setIsDownloading] = useState(false)

  const handleShare = useCallback(() => {
    const item = items[currentIndex]
    if (!item?.url) return

    if (!navigator.share) {
      onInfo("Sharing is not supported in this browser.")
      return
    }

    navigator
      .share({
        title: "Check out this image",
        url: item.url
      })
      .catch(error => {
        console.error("Error sharing:", error)
        onError("Failed to share image. Please try again.")
      })
  }, [currentIndex, items, onError, onInfo])

  const handleDownload = useCallback(async () => {
    if (isDownloading) return

    const item = items[currentIndex]
    if (!item?.url) return

    setIsDownloading(true)
    try {
      const response = await fetch(item.url)
      if (!response.ok) throw new Error("Failed to fetch image")

      const blob = await response.blob()
      const ext = blob.type.split("/")[1] || "jpg"
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement("a")

      link.href = blobUrl
      link.download = `image_${currentIndex + 1}.${ext}`
      link.click()

      URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error("Error downloading image:", error)
      onError("Failed to download image. Please try again.")
    } finally {
      setIsDownloading(false)
    }
  }, [currentIndex, isDownloading, items, onError])

  return { isDownloading, handleDownload, handleShare }
}

export { useGalleryMediaActions }
